import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Alert,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usuariosApi } from '../../api/usuarios'
import { UserManutencao } from '../../api/usuariosManutencao'

interface AlterarSenhasDialogProps {
  open: boolean
  onClose: () => void
  usuario: UserManutencao | null
}

const AlterarSenhasDialog: React.FC<AlterarSenhasDialogProps> = ({
  open,
  onClose,
  usuario,
}) => {
  const [tab, setTab] = useState(0)
  const [senhaGalatea, setSenhaGalatea] = useState('')
  const [confirmarSenhaGalatea, setConfirmarSenhaGalatea] = useState('')
  const [senhaThor, setSenhaThor] = useState('')
  const [senhaGalateaAmbas, setSenhaGalateaAmbas] = useState('')
  const [confirmarSenhaGalateaAmbas, setConfirmarSenhaGalateaAmbas] = useState('')
  const [senhaThorAmbas, setSenhaThorAmbas] = useState('')
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const alterarSenhaGalateaMutation = useMutation({
    mutationFn: ({ id, senha }: { id: number; senha: string }) =>
      usuariosApi.alterarSenhaGalatea(id, senha),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      onClose()
      resetForm()
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Erro ao alterar senha')
    },
  })

  const alterarSenhaThorMutation = useMutation({
    mutationFn: ({ id, senha }: { id: number; senha: string }) =>
      usuariosApi.alterarSenhaThor(id, senha),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      onClose()
      resetForm()
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Erro ao alterar senha ThorJava')
    },
  })

  const alterarAmbasSenhasMutation = useMutation({
    mutationFn: ({ id, senhaGalatea, senhaThor }: { id: number; senhaGalatea: string; senhaThor: string }) =>
      usuariosApi.alterarAmbasSenhas(id, senhaGalatea, senhaThor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios-manutencao'] })
      onClose()
      resetForm()
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Erro ao alterar senhas')
    },
  })

  const resetForm = () => {
    setSenhaGalatea('')
    setConfirmarSenhaGalatea('')
    setSenhaThor('')
    setSenhaGalateaAmbas('')
    setConfirmarSenhaGalateaAmbas('')
    setSenhaThorAmbas('')
    setError(null)
    setTab(0)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmitGalatea = () => {
    if (!usuario) return
    
    if (senhaGalatea.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres')
      return
    }
    
    if (senhaGalatea !== confirmarSenhaGalatea) {
      setError('As senhas não coincidem')
      return
    }

    setError(null)
    alterarSenhaGalateaMutation.mutate({ id: usuario.id, senha: senhaGalatea })
  }

  const handleSubmitThor = () => {
    if (!usuario) return
    
    if (senhaThor.length < 1) {
      setError('A senha ThorJava não pode estar vazia')
      return
    }

    if (senhaThor.length > 30) {
      setError('A senha ThorJava deve ter no máximo 30 caracteres')
      return
    }

    setError(null)
    alterarSenhaThorMutation.mutate({ id: usuario.id, senha: senhaThor })
  }

  const handleSubmitAmbas = () => {
    if (!usuario) return
    
    if (senhaGalateaAmbas.length < 6) {
      setError('A senha Galatea Web deve ter no mínimo 6 caracteres')
      return
    }
    
    if (senhaGalateaAmbas !== confirmarSenhaGalateaAmbas) {
      setError('As senhas Galatea Web não coincidem')
      return
    }

    if (senhaThorAmbas.length < 1) {
      setError('A senha ThorJava não pode estar vazia')
      return
    }

    if (senhaThorAmbas.length > 30) {
      setError('A senha ThorJava deve ter no máximo 30 caracteres')
      return
    }

    setError(null)
    alterarAmbasSenhasMutation.mutate({
      id: usuario.id,
      senhaGalatea: senhaGalateaAmbas,
      senhaThor: senhaThorAmbas,
    })
  }

  const isLoading =
    alterarSenhaGalateaMutation.isPending ||
    alterarSenhaThorMutation.isPending ||
    alterarAmbasSenhasMutation.isPending

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Alterar Senhas - {usuario?.name}
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Senha Galatea Web" />
          <Tab label="Senha ThorJava" />
          <Tab label="Ambas as Senhas" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {tab === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Altere apenas a senha do Galatea Web (usada para login no sistema web).
            </Typography>
            <TextField
              label="Nova senha Galatea Web"
              type="password"
              fullWidth
              margin="normal"
              value={senhaGalatea}
              onChange={(e) => setSenhaGalatea(e.target.value)}
              disabled={isLoading}
              helperText="Mínimo de 6 caracteres"
            />
            <TextField
              label="Confirmar nova senha"
              type="password"
              fullWidth
              margin="normal"
              value={confirmarSenhaGalatea}
              onChange={(e) => setConfirmarSenhaGalatea(e.target.value)}
              disabled={isLoading}
            />
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Altere apenas a senha do ThorJava (usada para login no sistema legado).
            </Typography>
            <TextField
              label="Nova senha ThorJava"
              type="password"
              fullWidth
              margin="normal"
              value={senhaThor}
              onChange={(e) => setSenhaThor(e.target.value)}
              disabled={isLoading}
              helperText="Máximo de 30 caracteres"
            />
          </Box>
        )}

        {tab === 2 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Altere ambas as senhas simultaneamente.
            </Typography>
            <TextField
              label="Nova senha Galatea Web"
              type="password"
              fullWidth
              margin="normal"
              value={senhaGalateaAmbas}
              onChange={(e) => setSenhaGalateaAmbas(e.target.value)}
              disabled={isLoading}
              helperText="Mínimo de 6 caracteres"
            />
            <TextField
              label="Confirmar senha Galatea Web"
              type="password"
              fullWidth
              margin="normal"
              value={confirmarSenhaGalateaAmbas}
              onChange={(e) => setConfirmarSenhaGalateaAmbas(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              label="Nova senha ThorJava"
              type="password"
              fullWidth
              margin="normal"
              value={senhaThorAmbas}
              onChange={(e) => setSenhaThorAmbas(e.target.value)}
              disabled={isLoading}
              helperText="Máximo de 30 caracteres"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={() => {
            if (tab === 0) handleSubmitGalatea()
            else if (tab === 1) handleSubmitThor()
            else handleSubmitAmbas()
          }}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? 'Alterando...' : 'Alterar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AlterarSenhasDialog





